import { Button } from '@/styles/buttons';
import {
	Column,
	Dialog,
	DialogContent,
	DialogCta,
	DialogHeader,
} from '@/styles/common';
import { produce } from 'immer';
import React, {
	ComponentProps,
	PropsWithChildren,
	useEffect,
	useRef,
	useState,
} from 'react';
import { Loader } from '../Loader';
import { SelectField } from '../fields';
import StringField from '../fields/StringField';

interface ModalProps {
	onCancel?: () => void;
	onConfirm: () => void;
	open?: boolean;
	title?: string;
	content?: string;
	confirmLabel?: string;
	cancelLabel?: string;
	onTop?: boolean;
	showCancel?: boolean;
	isError?: boolean;
}

const BaseModal: React.FC<ModalProps & PropsWithChildren> = ({
	onCancel,
	onConfirm,
	cancelLabel,
	confirmLabel,
	content,
	open,
	children,
	onTop = false,
	showCancel = true,
	isError = false,
}) => {
	const ref = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		if (open) {
			ref.current?.showModal();
		} else {
			ref.current?.close();
		}
	}, [open]);

	return (
		<Dialog
			onCancel={onCancel}
			ref={ref}
			style={{
				zIndex: onTop ? 1000 : undefined,
			}}>
			{children}
			<DialogCta>
				{showCancel && (
					<Button
						value='cancel'
						type='button'
						onClick={onCancel}
						variant='danger'>
						{cancelLabel ?? 'Cancel'}
					</Button>
				)}
				<Button
					onClick={onConfirm}
					type='button'
					value='submit'
					variant={isError ? 'danger' : 'primary'}>
					{confirmLabel ?? 'Confirm'}
				</Button>
			</DialogCta>
		</Dialog>
	);
};

type Params = Partial<Omit<ComponentProps<typeof BaseModal>, 'open'>>;

const confirmAction = {
	current: (props?: Params) => Promise.resolve(true),
};

export const modal = {
	editField: (props: IEditFieldParams) => {},
	editFields: <T extends TRowData>(props: IEditFieldsParams<T>) => {},
	info: (props: IMessageParams) => {},
	error: (props: IMessageParams) => {},
	blockScreen: (value: boolean) => {},
};

export const confirmModal = (props?: Params) => {
	return confirmAction.current(props);
};

export const Modal = () => {
	const [open, setOpen] = useState(false);
	const [blockScreen, setBlockScreen] = useState(false);
	const [editFieldParams, setEditFieldParams] = useState<
		IEditFieldModalParams | undefined
	>();
	const [editFieldsParams, setEditFieldsParams] = useState<
		IEditFieldsModalParams<any> | undefined
	>();
	const [messageParams, setMessageParams] = useState<
		IMessageModalParams | undefined
	>();

	useEffect(() => {
		modal.editField = (props: IEditFieldParams) => {
			setOpen(true);
			setEditFieldParams({
				...props,
				onCancel: () => {
					setOpen(true);
					setEditFieldParams(undefined);
				},
				closeModal: () => {
					setOpen(false);
					setEditFieldParams(undefined);
				},
				open,
			});
		};

		modal.editFields = <T extends TRowData>(props: IEditFieldsParams<T>) => {
			setOpen(true);
			setEditFieldsParams({
				...props,
				onCancel: () => {
					setOpen(true);
					setEditFieldsParams(undefined);
				},
				closeModal: () => {
					setOpen(false);
					setEditFieldsParams(undefined);
				},
				open,
			});
		};

		modal.info = (props: IMessageParams) => {
			const resp = new Promise<boolean>((resolve) => {
				setOpen(true);
				setMessageParams({
					title: props.title ?? 'Information',
					message: props.message ?? '',
					type: 'info',
					closeModal: () => {
						resolve(true);
						setMessageParams(undefined);
					},
				});
			});
			return resp;
		};

		modal.error = (props: IMessageParams) => {
			const resp = new Promise<boolean>((resolve) => {
				setOpen(true);
				setMessageParams({
					title: props.title ?? 'Erreur',
					message: props.message ?? '',
					type: 'error',
					closeModal: () => {
						resolve(true);
						setMessageParams(undefined);
					},
				});
			});
			return resp;
		};

		modal.blockScreen = setBlockScreen;
	}, []);

	const preventTab = (e: KeyboardEvent) => {
		if (e.key === 'Tab') {
			e.preventDefault();
		}
	};

	useEffect(() => {
		if (blockScreen) {
			window.document.addEventListener('keydown', preventTab);
		} else {
			window.document.removeEventListener('keydown', preventTab);
		}

		return () => {
			window.document.removeEventListener('keydown', preventTab);
		};
	}, [blockScreen]);

	if (blockScreen) {
		return <Loader opacity='0.2' />;
	}

	let elements = [];

	if (messageParams) {
		elements.push(<ModalMessage {...messageParams} open={open} key={1} />);
	}

	if (editFieldParams) {
		elements.push(<ModalEditField {...editFieldParams} open={open} key={2} />);
	}

	if (editFieldsParams) {
		elements.push(
			<ModalEditFields {...editFieldsParams} open={open} key={3} />
		);
	}

	if (elements.length) {
		return <>{elements}</>;
	}

	return null;
};

const ModalEditField: React.FC<IEditFieldModalParams> = ({
	type,
	list,
	closeModal,
	setValue,
	label,
	onCancel,
	title,
	value,
	open,
}) => {
	const [state, setState] = useState(value ?? null);
	let element;

	if (type === 'string') {
		element = (
			<StringField value={state as string} setValue={setState} label={label} />
		);
	}

	return (
		<BaseModal
			open={open}
			onCancel={onCancel}
			onConfirm={async () => {
				const r = await setValue(state);
				if (r) {
					closeModal();
				}
			}}>
			<div>
				<DialogHeader>
					<h2>{title ?? label}</h2>
				</DialogHeader>
				<DialogContent>{element}</DialogContent>
			</div>
		</BaseModal>
	);
};

const ModalEditFields = <T extends TRowData>({
	closeModal,
	onCancel,
	title,
	setData,
	setRow,
	data,
	open,
	description,
}: IEditFieldsModalParams<T>) => {
	const [localRow, setLocalRow] = useState<TRowData>({
		...data,
	});
	const setRowField = setRow
		? (fieldName: string, value: TValue) => {
				setLocalRow((_) =>
					produce(_, (draft) => {
						if (setData) {
							setData({ data: draft, fieldName, value });
						} else {
							draft[fieldName] = value;
						}
					})
				);
		  }
		: undefined;

	const colNames = Object.keys(description);

	return (
		<BaseModal
			open={open}
			onCancel={onCancel}
			onConfirm={async () => {
				const r = await setRow?.(localRow as T, setRowField);
				if (r) {
					closeModal();
				}
			}}>
			<div>
				<DialogHeader>
					<h2>{title ?? ''}</h2>
				</DialogHeader>
				<DialogContent>
					<Column className='full'>
						{colNames.map((colName) => {
							const descr = description[colName];
							const type = descr.type;

							if (type === 'string') {
								return (
									<StringField
										key={colName}
										value={localRow[colName] as string}
										setValue={(value) => {
											setRowField?.(colName, value);
										}}
										label={descr?.label ?? colName}
									/>
								);
							}
							if (type === 'list') {
								return (
									<SelectField
										key={colName}
										options={descr.list ?? []}
										value={localRow[colName] as string}
										setValue={(value) => {
											setRowField?.(colName, value);
										}}
										label={descr?.label ?? colName}
										title={descr?.title ?? 'Choisir une valeur'}
										variant={descr?.variant ?? 'secondary'}
									/>
								);
							}
						})}
					</Column>
				</DialogContent>
			</div>
		</BaseModal>
	);
};

const ModalMessage: React.FC<IMessageModalParams> = ({
	closeModal,
	type,
	message,
	title,
	open,
}) => {
	return (
		<BaseModal
			open={open}
			onConfirm={() => {
				closeModal();
			}}
			confirmLabel='Close'
			showCancel={false}
			isError={type === 'error'}
			onTop>
			<DialogHeader>
				<h2>{title}</h2>
			</DialogHeader>
			<DialogContent dangerouslySetInnerHTML={{ __html: message as string }} />
		</BaseModal>
	);
};

const ConfirmModal: React.FC = () => {
	const [open, setOpen] = useState(false);
	const [props, setProps] = useState<Params | undefined>();
	const resolveRef = useRef((v: boolean) => {});

	confirmAction.current = (props?: Params) =>
		new Promise((resolve) => {
			setProps(props);
			setOpen(true);
			resolveRef.current = resolve;
		});

	return (
		<BaseModal
			onCancel={() => {
				setOpen(false);
				resolveRef.current(false);
			}}
			onConfirm={() => {
				setOpen(false);
				resolveRef.current(true);
			}}
			open={open}
			{...props}>
			<DialogHeader>
				<h2>{props?.title ?? 'Confirmation'}</h2>
			</DialogHeader>
			<DialogContent>
				{props?.content ?? 'Are you sure you want to do this action ?'}
			</DialogContent>
		</BaseModal>
	);
};

export default ConfirmModal;
